import { useMutation } from '@tanstack/react-query'
import { ApiQueryKeys } from 'globals/constants'
import { useSnackbar } from 'notistack'
import { CrmCompanyLinkedContactsApi, CrmContactLinkedCompaniesApi } from 'sdk'

interface Props {
  companyId: string
}
const crmCompanyLinkedContacts = new CrmCompanyLinkedContactsApi()
const linkedCompaniesApi = new CrmContactLinkedCompaniesApi()
export const useCrmCompanyLinkedContacts = ({ companyId }: Props) => {
  const { enqueueSnackbar } = useSnackbar()

  // Fetch Company Linked Contacts
  const {
    mutate: getCompanyLinkedContacts,
    data: companyLinkedContacts,
    isPending: isGettingCompanyLinkedContacts,
  } = useMutation({
    mutationKey: [ApiQueryKeys.crm.contactLinkedCompanies.FetchLinkedContacts],
    mutationFn: () => crmCompanyLinkedContacts.getCompanyLinkedContacts(companyId),
    onError: (err) => enqueueSnackbar(err.message),
  })

  // Fetch Company UnLinked Contacts
  const {
    mutate: getCompanyUnLinkedContacts,
    data: companyUnLinkedContacts,
    isPending: isGettingCompanyUnLinkedContacts,
  } = useMutation({
    mutationKey: [ApiQueryKeys.crm.contactLinkedCompanies.FetchUnlinkedContacts],
    mutationFn: () => crmCompanyLinkedContacts.getCompanyUnlinkedContact(companyId),
    onError: (err) => enqueueSnackbar(err.message),
  })
  // Adding a new link
  const { isPending: isAddingLinkedCompany, mutateAsync: linkCompany } = useMutation({
    mutationFn: linkedCompaniesApi.linkCompany,
    mutationKey: [ApiQueryKeys.crm.contactLinkedCompanies.AddLinkedCompany],
    onSuccess: () => {
      enqueueSnackbar('Company has been added', { variant: 'success' })
      getCompanyUnLinkedContacts()
      getCompanyLinkedContacts()
    },
  })

  // Deleting Linked Company
  const { mutate: UnlinkCompany, isPending: isUnlinking } = useMutation({
    mutationFn: (contactId: string) => linkedCompaniesApi.unlinkContactLinkedCompany(contactId, companyId),
    mutationKey: [ApiQueryKeys.crm.contactLinkedCompanies.RemoveLinkedCompany],
    onSuccess: () => {
      enqueueSnackbar('Contact has been removed', { variant: 'success' })
      getCompanyUnLinkedContacts()
      getCompanyLinkedContacts()
    },
  })

  return {
    companyLinkedContacts,
    companyUnLinkedContacts,
    isGettingCompanyLinkedContacts,
    isGettingCompanyUnLinkedContacts,
    isAddingLinkedCompany,
    isUnlinking,
    getCompanyLinkedContacts,
    getCompanyUnLinkedContacts,
    UnlinkCompany,
    linkCompany,
  }
}
